import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { assetService } from "../../Core/Api/services";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { GetChildRecursivelyObjectById } from "../../Core/Utils/asset-object-utils";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { RootState } from "../redux-store";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { createAppAsyncThunk } from "../with-types";

export enum TreeContext {
  TitlePanel = "titlePanel",
  AssetHierarchy = "assetHierarchy",
  MoveDistrib = "moveDistrib",
  DriveTreeview = "driveTreeview",
  MoveDrive = "moveDrive",
  AssetTarPreview = "assetTarPreview",
  Timetool = "timetool",
}

export const TreeContexts: { key: TreeContext; authorizedObjectTypes: ObjectType[] }[] = [
  {
    key: TreeContext.TitlePanel,
    authorizedObjectTypes: Object.values(ObjectType).filter((x) => x !== ObjectType.UploadContainer),
  },
  {
    key: TreeContext.AssetHierarchy,
    authorizedObjectTypes: Object.values(ObjectType).filter(
      (x) => x !== ObjectType.VirtualHead && x !== ObjectType.UploadContainer
    ),
  },
  {
    key: TreeContext.MoveDistrib,
    authorizedObjectTypes: Object.values(ObjectType).filter(
      (x) => x === ObjectType.Group || x === ObjectType.Folder || x === ObjectType.GraphSkeleton
    ),
  },
  {
    key: TreeContext.DriveTreeview,
    authorizedObjectTypes: [ObjectType.Folder, ObjectType.VirtualHead, ObjectType.GraphSkeleton],
  },
  {
    key: TreeContext.MoveDrive,
    authorizedObjectTypes: [ObjectType.Folder, ObjectType.VirtualHead, ObjectType.GraphSkeleton],
  },
  {
    key: TreeContext.AssetTarPreview,
    authorizedObjectTypes: Object.values(ObjectType),
  },
  {
    key: TreeContext.Timetool,
    authorizedObjectTypes: Object.values(ObjectType).filter(
      (x) => x === ObjectType.Group || x === ObjectType.Folder || x === ObjectType.GraphSkeleton
    ),
  },
];

interface TreeContextState {
  treeObj: DbAssetObjectInfos | undefined;
  currentObjectId?: number;
  authorizedObjectTypes: ObjectType[];
  expandedIds: number[];
  selectedIds: number[];
  rootStatus: "idle" | "pending" | "fulfilled" | "rejected";
  childrenStatus: "idle" | "pending" | "fulfilled" | "rejected";
  currentRequestId: string | null;
  error: string | null;
}

interface TreeState {
  [key: string]: TreeContextState;
}

const treeContexts = TreeContexts.reduce((acc, context) => {
  acc[context.key] = {
    treeObj: undefined,
    authorizedObjectTypes: context.authorizedObjectTypes,
    expandedIds: [],
    selectedIds: [],
    rootStatus: "idle",
    childrenStatus: "idle",
    currentRequestId: null,
    error: null,
  } as TreeContextState;

  return acc;
}, {} as TreeState);

const initialState: TreeState = treeContexts;

// Thunks
interface FetchChildrenParams {
  context: keyof TreeState;
  id: number;
}

interface FetchInitialTreeParams {
  context: keyof TreeState;
  rootId: number;
  currentId?: number;
}
interface RefreshTreeParams {
  context: keyof TreeState;
  currentId?: number;
}

// Fetch initial tree from DbAssetObjectInfos ID
export const fetchInitialTree = createAppAsyncThunk(
  "objectTree/FetchInitialTree",
  async (params: FetchInitialTreeParams, thunkAPI) => {
    const { context, rootId, currentId } = params;
    const treeSlice = (thunkAPI.getState() as RootState).treeSlice[context];

    if (rootId === treeSlice.treeObj?.id) return;

    const root = await assetService.GetObjectLightById(rootId, 1);
    if (!root || !root.graph) throw new Error(`Object ${rootId} not found`);

    //Filter out children that are not in authorizedObjectTypes
    if (root.graph.childObjects && treeSlice.authorizedObjectTypes.length > 0) {
      root.graph.childObjects = root.graph.childObjects.filter((x) => treeSlice.authorizedObjectTypes.includes(x.type));
    }

    thunkAPI.dispatch(SetTreeObj({ obj: root.graph, context: context as TreeContext }));

    if (currentId) {
      thunkAPI.dispatch(SetCurrentObjectId({ context: context as TreeContext, id: currentId }));
    } else {
      thunkAPI.dispatch(SetExpandedItems({ context: context as TreeContext, expandedIds: [rootId] }));
      return;
    }

    const currentAsset = await assetService.GetObjectLightById(currentId, 0);
    if (!currentAsset.graph) throw new Error(`Object ${currentId} not found`);

    const graph = currentAsset.graph;
    if (!graph.allParentObjectIds || graph.allParentObjectIds.length === 0)
      throw new Error(`Object ${currentId} has no parents`);

    thunkAPI.dispatch(
      SetExpandedItems({ context: context as TreeContext, expandedIds: graph.allParentObjectIds, replace: true })
    );

    for (const [index, parentId] of graph.allParentObjectIds.entries()) {
      // Look for parent in tree
      const parent = GetChildRecursivelyObjectById(treeSlice.treeObj, parentId);

      // If parent not in tree yet, load and add it to tree
      if (parent === null && parentId !== root.graph.id) {
        const parent = await assetService.GetObjectLightById(parentId, 1);
        if (parent && parent.graph) {
          thunkAPI.dispatch(
            AddChildrenToTreeObj({
              parentId: parent.graph.id,
              children: parent.graph.childObjects ?? [],
              context: context as TreeContext,
            })
          );
        }
      }

      // On last parent added, push current object to the tree
      if (index === graph.allParentObjectIds.length - 1) {
        thunkAPI.dispatch(
          AddChildrenToTreeObj({
            parentId: parentId,
            children: [graph],
            context: context as TreeContext,
          })
        );
      }
    }
  }
);

// On item expasion, fetch children and store them in the tree
export const fetchAndStoreChildrenInTreeById = createAppAsyncThunk(
  "objectTree/FetchAndStoreChildrenInTreeById",
  async (params: FetchChildrenParams, thunkAPI): Promise<DbAssetObjectInfos[]> => {
    const { context, id } = params;
    const { requestId } = thunkAPI;
    const { childrenStatus, currentRequestId } = thunkAPI.getState().treeSlice[context];
    const treeSlice = (thunkAPI.getState() as RootState).treeSlice[context];
    if (!treeSlice.treeObj || currentRequestId !== requestId) return [];

    const target = GetChildRecursivelyObjectById(treeSlice.treeObj, id);
    if (target?.childCount === 0 || target?.childObjects) return [];

    const children = (
      await assetService.GetChildrenByObjectId(
        id,
        GlobalConst.MAX_CHILDREN,
        [],
        0,
        thunkAPI.getState().treeSlice[context].authorizedObjectTypes
      )
    )?.children;

    if (children === null || children === undefined || children.length === 0) return [];

    return children;
  }
);

// Refresh tree : essentially a copy of FetchInitialTree but taking the current TreeObj as entry parameter
export const refreshTree = createAppAsyncThunk(
  "objectTree/RefreshTree",
  async (params: RefreshTreeParams, thunkAPI) => {
    const { context, currentId } = params;
    const treeSlice = (thunkAPI.getState() as RootState).treeSlice[context];

    // Reset selected items on refresh
    thunkAPI.dispatch(SetSelectedItems({ context: context as TreeContext, selectedIds: [], replace: true }));

    if (!treeSlice.treeObj) throw new Error(`Can't refresh : Tree is not initialized`);
    const rootId = treeSlice.treeObj.id;

    const root = await assetService.GetObjectLightById(rootId, 1);
    if (!root || !root.graph) throw new Error(`Object ${rootId} not found`);

    //Filter out children that are not in authorizedObjectTypes
    if (root.graph.childObjects) {
      root.graph.childObjects = root.graph.childObjects.filter((x) => treeSlice.authorizedObjectTypes.includes(x.type));
    }

    thunkAPI.dispatch(SetTreeObj({ obj: root.graph, context: context as TreeContext }));

    if (currentId) {
      thunkAPI.dispatch(SetCurrentObjectId({ context: context as TreeContext, id: currentId }));
    } else {
      thunkAPI.dispatch(SetExpandedItems({ context: context as TreeContext, expandedIds: [rootId] }));
      return;
    }

    const currentAsset = await assetService.GetObjectLightById(currentId, 0);
    if (!currentAsset.graph) throw new Error(`Object ${currentId} not found`);

    const graph = currentAsset.graph;
    if (!graph.allParentObjectIds || graph.allParentObjectIds.length === 0)
      throw new Error(`Object ${currentId} has no parents`);

    thunkAPI.dispatch(SetExpandedItems({ context: context as TreeContext, expandedIds: graph.allParentObjectIds }));

    for (const [index, parentId] of graph.allParentObjectIds.entries()) {
      // Look for parent in tree
      const parent = GetChildRecursivelyObjectById(treeSlice.treeObj, parentId);

      // If parent not in tree yet, load and add it to tree
      if (parent === null && parentId !== root.graph.id) {
        const parent = await assetService.GetObjectLightById(parentId, 1);
        if (parent && parent.graph) {
          thunkAPI.dispatch(
            AddChildrenToTreeObj({
              parentId: parent.graph.id,
              children: parent.graph.childObjects ?? [],
              context: context as TreeContext,
            })
          );
        }
      }

      // On last parent added, push current object to the tree
      if (index === graph.allParentObjectIds.length - 1) {
        thunkAPI.dispatch(
          AddChildrenToTreeObj({
            parentId: parentId,
            children: [graph],
            context: context as TreeContext,
          })
        );
      }
    }
  }
);

const treeSlice = createSlice({
  name: "Tree",
  initialState,
  reducers: {
    SetTreeObj: (state, action: PayloadAction<{ obj: DbAssetObjectInfos; context: TreeContext }>) => {
      const obj = JSON.parse(JSON.stringify(action.payload.obj));

      // Recursive function to filter children
      const filterChildren = (node: DbAssetObjectInfos): DbAssetObjectInfos => {
        if (!node.childObjects || node.childObjects.length === 0) return node;

        node.childObjects = node.childObjects
          .filter((child) => state[action.payload.context].authorizedObjectTypes.includes(child.type))
          .map(filterChildren); // Recursively filter children

        return node;
      };

      state[action.payload.context].treeObj = filterChildren(obj);
    },
    SetCurrentObjectId: (state, action: PayloadAction<{ context: TreeContext; id: number | undefined }>) => {
      state[action.payload.context].currentObjectId = action.payload.id;
    },
    AddChildrenToTreeObj: (
      state,
      action: PayloadAction<{ parentId: number; children: DbAssetObjectInfos[]; context: TreeContext }>
    ) => {
      if (state[action.payload.context].treeObj === null || state[action.payload.context].treeObj === undefined) return;

      let parent: DbAssetObjectInfos | null = null;

      if (action.payload.parentId === state[action.payload.context].treeObj?.id) {
        parent = state[action.payload.context].treeObj ?? null;
      } else {
        parent = GetChildRecursivelyObjectById(state[action.payload.context].treeObj, action.payload.parentId);
      }

      if (parent === null) return;

      if (parent.childObjects === null || parent.childObjects === undefined) {
        parent.childObjects = [];
      }

      const toAdd: DbAssetObjectInfos[] = action.payload.children.filter(
        (x) => parent.childObjects?.find((y) => y.id === x.id) === undefined
      );

      parent.childObjects = (parent.childObjects ? [...parent.childObjects] : []).concat(
        toAdd.filter((x) => state[action.payload.context].authorizedObjectTypes.includes(x.type))
      );
    },
    RemoveChildrenFromTreeObj: (state, action: PayloadAction<{ children: number[]; context: TreeContext }>) => {
      if (state[action.payload.context].treeObj === null || state[action.payload.context].treeObj === undefined) return;

      action.payload.children.forEach((childId) => {
        const assetToRemove = GetChildRecursivelyObjectById(state[action.payload.context].treeObj, childId);
        if (assetToRemove === null) return;

        let parent: DbAssetObjectInfos | null = null;

        if (assetToRemove.parentObjectId === state[action.payload.context].treeObj?.id) {
          parent = state[action.payload.context].treeObj ?? null;
        } else {
          parent = GetChildRecursivelyObjectById(
            state[action.payload.context].treeObj,
            assetToRemove.parentObjectId ?? 0
          );
        }

        if (parent && parent.childObjects) {
          parent.childObjects = parent.childObjects.filter((x) => !(x.id === childId));
        }
      });
    },
    MoveChildrenInTreeObj: (
      state,
      action: PayloadAction<{ children: number[]; parentObjectId: number; context: TreeContext }>
    ) => {
      if (state[action.payload.context].treeObj === null || state[action.payload.context].treeObj === undefined) return;
      action.payload.children.forEach((childId) => {
        const assetToMove = GetChildRecursivelyObjectById(state[action.payload.context].treeObj, childId);

        if (!assetToMove) return;

        const oldParent =
          assetToMove.parentObjectId === state[action.payload.context].treeObj!.id
            ? state[action.payload.context].treeObj
            : GetChildRecursivelyObjectById(state[action.payload.context].treeObj, assetToMove.parentObjectId ?? 0);
        const newParent =
          action.payload.parentObjectId === state[action.payload.context].treeObj!.id
            ? state[action.payload.context].treeObj
            : GetChildRecursivelyObjectById(state[action.payload.context].treeObj, action.payload.parentObjectId);

        if (!oldParent || !newParent) return;
        if (oldParent.childObjects?.find((x) => x.id === childId) === undefined) return;
        if (!newParent.childObjects) newParent.childObjects = [];
        assetToMove.parentObjectId = newParent.id;
        oldParent.childObjects.splice(oldParent.childObjects.indexOf(assetToMove), 1);
        newParent.childObjects.push(assetToMove);
      });

      state = { ...state };
    },
    RenameChildInTreeObj: (state, action: PayloadAction<{ objId: number; newName: string; context: TreeContext }>) => {
      if (state[action.payload.context].treeObj === null || state[action.payload.context].treeObj === undefined) return;
      const assetToRename = GetChildRecursivelyObjectById(state[action.payload.context].treeObj, action.payload.objId);
      if (assetToRename === null) return;

      assetToRename.name = action.payload.newName;

      state = { ...state };
    },
    SetExpandedItems: (
      state,
      action: PayloadAction<{ context: TreeContext; expandedIds: number[]; replace?: boolean }>
    ) => {
      const context = action.payload.context;
      if (action.payload.replace) state[context].expandedIds = action.payload.expandedIds;
      else {
        state[context].expandedIds = [...new Set([...state[context].expandedIds, ...action.payload.expandedIds])];
      }
    },
    SetSelectedItems: (
      state,
      action: PayloadAction<{ context: TreeContext; selectedIds: number[]; replace?: boolean }>
    ) => {
      const context = action.payload.context;
      if (action.payload.replace) state[context].selectedIds = action.payload.selectedIds;
      else {
        state[context].selectedIds = [...state[context].selectedIds, ...action.payload.selectedIds];
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitialTree.pending, (state, action) => {
      const context = action.meta.arg.context;
      state[context].rootStatus = "pending";
    });
    builder.addCase(fetchInitialTree.rejected, (state, action) => {
      const context = action.meta.arg.context;
      state[context].rootStatus = "rejected";
      state[context].error = action.error.message ?? null;
    });
    builder.addCase(fetchInitialTree.fulfilled, (state, action) => {
      const context = action.meta.arg.context;
      state[context].rootStatus = "fulfilled";
    });
    builder.addCase(fetchAndStoreChildrenInTreeById.pending, (state, action) => {
      const context = action.meta.arg.context;
      state[context].childrenStatus = "pending";
      state[context].currentRequestId = action.meta.requestId;
    });
    builder.addCase(fetchAndStoreChildrenInTreeById.rejected, (state, action) => {
      const context = action.meta.arg.context;
      state[context].childrenStatus = "rejected";
      state[context].currentRequestId = null;
      state[context].error = action.error.message ?? null;
    });
    builder.addCase(fetchAndStoreChildrenInTreeById.fulfilled, (state, action) => {
      const context = action.meta.arg.context;
      const parentId = action.meta.arg.id;

      if (state[context].currentRequestId !== action.meta.requestId) return;

      const parent = GetChildRecursivelyObjectById(state[context].treeObj, parentId);

      if (!parent) return;
      if (parent.childObjects === null || parent.childObjects === undefined) {
        parent.childObjects = [...action.payload];
      } else {
        parent.childObjects = [...parent.childObjects, ...action.payload];
      }
      state[context].childrenStatus = "fulfilled";
      state[context].currentRequestId = null;
    });
  },
});

export const {
  SetTreeObj,
  SetCurrentObjectId,
  AddChildrenToTreeObj,
  MoveChildrenInTreeObj,
  RemoveChildrenFromTreeObj,
  RenameChildInTreeObj,
  SetExpandedItems,
  SetSelectedItems,
} = treeSlice.actions;

export const GetTreeObj =
  (context: TreeContext) =>
  (state: RootState): DbAssetObjectInfos | undefined =>
    state.treeSlice[context].treeObj;

export const GetTreeStatus =
  (context: TreeContext) =>
  (state: RootState): string =>
    state.treeSlice[context].rootStatus;

export const GetTreeHasObject =
  (context: TreeContext) =>
  (state: RootState): boolean =>
    state.treeSlice[context].treeObj !== undefined && state.treeSlice[context].treeObj !== null;

export const GetTreeCurrentObjectId =
  (context: TreeContext) =>
  (state: RootState): number | undefined =>
    state.treeSlice[context].currentObjectId;

export const GetExpandedItems =
  (context: TreeContext) =>
  (state: RootState): number[] =>
    state.treeSlice[context].expandedIds;

export const GetSelectedItems =
  (context: TreeContext) =>
  (state: RootState): number[] =>
    state.treeSlice[context].selectedIds;

export default treeSlice.reducer;
